// TPS-635 Allow user to minimize Pat AI agent
/**
 * @file et-chat-launcher-minimize.js
 * @see docs/ET_CHAT_LAUNCHER_OVERRIDE_CONTEXT.md
 *
 * Augments app-injected #et-chat-embed-toggle: FAB + minimize (×) → right-edge tab.
 * Never replace or nest buttons inside the toggle node.
 */
(function () {
  // --- Constants (keep in sync with assets/tps-custom.css) ---

  const ET_CHAT_LAUNCHER_STORAGE_KEY = 'etChatLauncherMode';
  /** Tab preference lifetime (ms); not extended on revisit. */
  const ET_CHAT_LAUNCHER_TTL_MS = 30 * 60 * 1000;
  /**
   * TPS-635: Keep below .section-header (3), facets (100), cart (1000).
   * Must match --et-chat-launcher-z-index fallback in tps-custom.css.
   * @see docs/ET_CHAT_LAUNCHER_OVERRIDE_CONTEXT.md
   */
  const ET_CHAT_LAUNCHER_Z_INDEX = 2;
  /** Tab dimensions; mirror .et-chat-launcher-shell--tab #et-chat-embed-toggle in CSS */
  const ET_CHAT_TAB_WIDTH_PX = 30;
  const ET_CHAT_TAB_HEIGHT_PX = 130;
  const ET_CHAT_FAB_TOOLTIP_HEADING = "Hi, I'm Pat!";
  const ET_CHAT_FAB_TOOLTIP_BODY =
    'I can help you find the perfect patio furniture for your space and answer any questions you have.';

  const TOGGLE_ID = 'et-chat-embed-toggle';
  const FAB_STYLE_DEBOUNCE_MS = 50;
  const RESIZE_END_MS = 150;

  const PANEL_ID_CANDIDATES = [
    'et-chat-embed',
    'et-chat-embed-panel',
    'et-chat-widget',
    'et-chat-container',
  ];

  let shellRef = null;
  let anchorRef = null;
  let toggleRef = null;
  let minimizeRef = null;
  let panelRef = null;
  let styleObserver = null;
  let panelObserver = null;
  let fabStyleDebounceTimer = null;
  let resizeEndTimer = null;
  let isApplyingLauncherMode = false;
  let isWindowResizing = false;
  let liveRegion = null;

  // --- Storage (localStorage + TTL) ---

  /**
   * @returns {'fab'|'tab'}
   */
  function getStoredMode() {
    try {
      const raw = localStorage.getItem(ET_CHAT_LAUNCHER_STORAGE_KEY);
      if (!raw) return 'fab';

      const data = JSON.parse(raw);
      if (!data || data.mode !== 'tab' || typeof data.expiresAt !== 'number') {
        localStorage.removeItem(ET_CHAT_LAUNCHER_STORAGE_KEY);
        return 'fab';
      }

      if (Date.now() >= data.expiresAt) {
        localStorage.removeItem(ET_CHAT_LAUNCHER_STORAGE_KEY);
        return 'fab';
      }

      return 'tab';
    } catch {
      localStorage.removeItem(ET_CHAT_LAUNCHER_STORAGE_KEY);
      return 'fab';
    }
  }

  function setStoredMode(mode) {
    if (mode === 'tab') {
      localStorage.setItem(
        ET_CHAT_LAUNCHER_STORAGE_KEY,
        JSON.stringify({
          mode: 'tab',
          expiresAt: Date.now() + ET_CHAT_LAUNCHER_TTL_MS,
        })
      );
      return;
    }
    localStorage.removeItem(ET_CHAT_LAUNCHER_STORAGE_KEY);
  }

  // --- DOM helpers ---

  function findToggle() {
    return document.getElementById(TOGGLE_ID);
  }

  function isAugmented(toggle) {
    return Boolean(toggle?.closest('[data-et-launcher-augmented="true"]'));
  }

  /** True when embed has restored circular FAB inline styles on the toggle. */
  function isFabGeometry(toggle) {
    const radius = toggle.style.borderRadius || getComputedStyle(toggle).borderRadius;
    const width =
      parseFloat(toggle.style.width) || parseFloat(getComputedStyle(toggle).width);
    return radius.includes('50%') || width >= 80;
  }

  // --- ARIA (chat panel + live region) ---

  function findChatPanel() {
    for (let i = 0; i < PANEL_ID_CANDIDATES.length; i += 1) {
      const el = document.getElementById(PANEL_ID_CANDIDATES[i]);
      if (el?.id) return el;
    }
    return document.querySelector(
      '[id^="et-chat-"][id*="panel"]:not(#' + TOGGLE_ID + ')'
    );
  }

  function isPanelOpen(panel) {
    if (!panel) return false;
    if (panel.hasAttribute('hidden')) return false;
    if (panel.getAttribute('aria-hidden') === 'true') return false;
    const style = getComputedStyle(panel);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function syncAriaExpanded() {
    if (!toggleRef || !panelRef?.id) return;
    toggleRef.setAttribute('aria-controls', panelRef.id);
    toggleRef.setAttribute('aria-expanded', isPanelOpen(panelRef) ? 'true' : 'false');
  }

  function setupPanelAria() {
    panelRef = findChatPanel();
    if (!panelRef?.id) {
      panelRef = null;
      if (toggleRef?.hasAttribute('aria-controls')) {
        toggleRef.removeAttribute('aria-controls');
        toggleRef.removeAttribute('aria-expanded');
      }
      return;
    }

    syncAriaExpanded();

    if (panelObserver) panelObserver.disconnect();
    panelObserver = new MutationObserver(syncAriaExpanded);
    panelObserver.observe(panelRef, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });
  }

  function ensureLiveRegion() {
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announceMinimized() {
    const region = ensureLiveRegion();
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = 'Chat launcher minimized';
    });
  }

  function applyLauncherZIndex(shell) {
    document.documentElement.style.setProperty(
      '--et-chat-launcher-z-index',
      String(ET_CHAT_LAUNCHER_Z_INDEX)
    );
    if (shell) {
      shell.style.setProperty('z-index', String(ET_CHAT_LAUNCHER_Z_INDEX));
    }
  }

  // --- Position + z-index (shell owns viewport anchoring) ---

  function transferPositionToShell(shell, toggle) {
    syncEmbedPositionToShell(shell, toggle);
    if (!shell.style.position) shell.style.setProperty('position', 'fixed');
    applyLauncherZIndex(shell);
  }

  /**
   * Move bottom/right/etc. from toggle to shell so embed resize updates stay aligned.
   */
  function syncEmbedPositionToShell(shell, toggle) {
    if (!shell || !toggle) return;

    const props = ['bottom', 'right', 'left', 'top'];
    props.forEach((prop) => {
      if (toggle.style[prop]) {
        shell.style[prop] = toggle.style[prop];
        toggle.style[prop] = '';
      }
    });

    if (toggle.style.position) {
      toggle.style.position = '';
    }

    const computedPosition = getComputedStyle(toggle).position;
    if (computedPosition === 'fixed' || computedPosition === 'absolute') {
      toggle.style.setProperty('position', 'relative');
    }
  }

  function ensureFabShellDefaults(shell) {
    if (!shell.style.bottom && !shell.style.top) {
      shell.style.setProperty('bottom', '60px');
    }
    if (!shell.style.right && !shell.style.left) {
      shell.style.setProperty('right', '20px');
    }
    shell.style.setProperty('left', 'auto');
    shell.style.setProperty('top', 'auto');
    shell.style.setProperty('transform', 'none');
  }

  function decorateAvatar(toggle) {
    const img = toggle.querySelector('img');
    if (!img) return;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
  }

  /**
   * Prevents MutationObserver loops while applyTabMode/applyFabMode set inline styles.
   */
  function runLauncherApply(applyFn) {
    if (isApplyingLauncherMode) return;
    isApplyingLauncherMode = true;
    try {
      applyFn();
    } finally {
      isApplyingLauncherMode = false;
    }
  }

  // --- Launcher modes (FAB vs tab) ---

  function needsTabStyleFix(toggle) {
    if (!toggle || getStoredMode() !== 'tab') return false;
    if (!shellRef?.classList.contains('et-chat-launcher-shell--tab')) return true;
    return isFabGeometry(toggle);
  }

  function applyTabMode() {
    runLauncherApply(applyTabModeImpl);
  }

  function applyTabModeImpl() {
    const shell = shellRef;
    const toggle = toggleRef;
    if (!shell || !toggle) return;

    shell.classList.remove('et-chat-launcher-shell--fab');
    shell.classList.add('et-chat-launcher-shell--tab');
    toggle.classList.add('et-chat-launcher--tab');

    toggle.setAttribute('aria-label', 'Open AI Chat');

    if (minimizeRef) minimizeRef.hidden = true;

    decorateAvatar(toggle);

    const label = toggle.querySelector('.et-chat-launcher-tab-label');
    if (label) {
      label.hidden = false;
      label.setAttribute('aria-hidden', 'true');
    }

    shell.style.setProperty('right', '0');
    shell.style.setProperty('left', 'auto');
    shell.style.setProperty('top', '50%');
    shell.style.setProperty('bottom', 'auto');
    shell.style.setProperty('transform', 'translateY(-50%)');

    toggle.style.setProperty('position', 'relative');
    toggle.style.setProperty('width', ET_CHAT_TAB_WIDTH_PX + 'px');
    toggle.style.setProperty('height', ET_CHAT_TAB_HEIGHT_PX + 'px');
    toggle.style.setProperty('border-radius', '0');
    toggle.style.setProperty('bottom', 'auto');
    toggle.style.setProperty('right', 'auto');
    toggle.style.setProperty('top', 'auto');
    toggle.style.setProperty('left', 'auto');
    toggle.style.setProperty('transform', 'none');
    toggle.style.setProperty('padding', '0');
    applyLauncherZIndex(shell);
  }

  function applyFabMode() {
    runLauncherApply(applyFabModeImpl);
  }

  function applyFabModeImpl() {
    const shell = shellRef;
    const toggle = toggleRef;
    if (!shell || !toggle) return;

    shell.classList.remove('et-chat-launcher-shell--tab');
    shell.classList.add('et-chat-launcher-shell--fab');
    toggle.classList.remove('et-chat-launcher--tab');

    toggle.setAttribute('aria-label', 'Open chat');

    if (minimizeRef) minimizeRef.hidden = false;

    decorateAvatar(toggle);

    const label = toggle.querySelector('.et-chat-launcher-tab-label');
    if (label) {
      label.hidden = true;
      label.setAttribute('aria-hidden', 'true');
    }

    syncEmbedPositionToShell(shell, toggle);
    ensureFabShellDefaults(shell);

    toggle.style.setProperty('position', 'relative');
    toggle.style.setProperty('width', '85px');
    toggle.style.setProperty('height', '85px');
    toggle.style.setProperty('border-radius', '50%');
    toggle.style.setProperty('padding', '0');
    toggle.style.setProperty('transform', 'none');
    applyLauncherZIndex(shell);
  }

  // --- Embed sync (observers + resize) ---

  function scheduleFabStyleSync() {
    clearTimeout(fabStyleDebounceTimer);
    fabStyleDebounceTimer = setTimeout(() => {
      if (isApplyingLauncherMode || isWindowResizing || getStoredMode() !== 'fab') return;
      applyFabMode();
    }, FAB_STYLE_DEBOUNCE_MS);
  }

  function correctTabModeIfNeeded() {
    const toggle = toggleRef;
    if (!toggle || !needsTabStyleFix(toggle)) return;
    requestAnimationFrame(() => {
      if (!needsTabStyleFix(toggleRef)) return;
      applyTabMode();
    });
  }

  /** React to embed rewriting toggle style/class; debounce FAB, immediate tab fix. */
  function onToggleMutated() {
    if (isApplyingLauncherMode) return;

    if (getStoredMode() === 'tab') {
      correctTabModeIfNeeded();
      return;
    }

    if (isWindowResizing) return;
    scheduleFabStyleSync();
  }

  function setupStyleObserver(toggle) {
    if (styleObserver) styleObserver.disconnect();

    styleObserver = new MutationObserver(() => {
      onToggleMutated();
    });

    styleObserver.observe(toggle, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  function setupResizeHandler() {
    window.addEventListener('resize', () => {
      if (!toggleRef) return;

      isWindowResizing = true;
      clearTimeout(resizeEndTimer);

      if (getStoredMode() === 'tab') {
        correctTabModeIfNeeded();
      }

      resizeEndTimer = setTimeout(() => {
        isWindowResizing = false;
        if (!toggleRef) return;

        if (getStoredMode() === 'tab') {
          if (needsTabStyleFix(toggleRef)) applyTabMode();
        } else {
          applyFabMode();
        }
      }, RESIZE_END_MS);
    });
  }

  // --- Init ---

  // TPS-635 Allow user to minimize Pat AI agent
  function initOnToggle(toggle) {
    if (!toggle || isAugmented(toggle)) return;

    const shell = document.createElement('div');
    shell.className = 'et-chat-launcher-shell et-chat-launcher-shell--fab';
    shell.setAttribute('data-et-launcher-augmented', 'true');
    shell.setAttribute('role', 'group');
    shell.setAttribute('aria-label', 'Chat launcher');

    const parent = toggle.parentNode;
    if (!parent) return;

    parent.insertBefore(shell, toggle);

    const anchor = document.createElement('div');
    anchor.className = 'et-chat-launcher-fab-anchor';
    shell.appendChild(anchor);
    anchor.appendChild(toggle);
    transferPositionToShell(shell, toggle);

    const minimize = document.createElement('button');
    minimize.type = 'button';
    minimize.id = 'et-chat-launcher-minimize';
    minimize.className = 'et-chat-launcher-minimize';
    minimize.setAttribute('aria-label', 'Minimize chat launcher');
    minimize.innerHTML = '<span aria-hidden="true">×</span>';
    anchor.insertBefore(minimize, toggle);

    let label = toggle.querySelector('.et-chat-launcher-tab-label');
    if (!label) {
      label = document.createElement('span');
      label.className = 'et-chat-launcher-tab-label';
      label.textContent = 'Ask Pat';
      label.hidden = true;
      toggle.appendChild(label);
    }

    if (!toggle.getAttribute('aria-label')) {
      toggle.setAttribute('aria-label', 'Open chat');
    }

    if (!anchor.querySelector('.et-chat-launcher-fab-tooltip')) {
      const tooltip = document.createElement('span');
      tooltip.className = 'et-chat-launcher-fab-tooltip';
      tooltip.setAttribute('aria-hidden', 'true');

      const heading = document.createElement('strong');
      heading.className = 'et-chat-launcher-fab-tooltip__heading';
      heading.textContent = ET_CHAT_FAB_TOOLTIP_HEADING;

      const body = document.createElement('span');
      body.className = 'et-chat-launcher-fab-tooltip__body';
      body.textContent = ET_CHAT_FAB_TOOLTIP_BODY;

      tooltip.appendChild(heading);
      tooltip.appendChild(body);
      anchor.appendChild(tooltip);
    }

    decorateAvatar(toggle);

    shellRef = shell;
    anchorRef = anchor;
    toggleRef = toggle;
    minimizeRef = minimize;

    minimize.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setStoredMode('tab');
      applyTabMode();
      announceMinimized();
      toggle.focus();
    });

    setupStyleObserver(toggle);
    setupResizeHandler();
    setupPanelAria();

    if (getStoredMode() === 'tab') {
      applyTabMode();
    } else {
      applyFabMode();
    }
  }

  function tryInit() {
    const toggle = findToggle();
    if (toggle && !isAugmented(toggle)) {
      initOnToggle(toggle);
    }
  }

  function start() {
    tryInit();

    const bodyObserver = new MutationObserver(() => {
      tryInit();
      if (!panelRef && toggleRef) setupPanelAria();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
